// Demo Session API Client
// Communicates with demo-session Edge Function

import { publicAnonKey } from '../../utils/supabase/info';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://uyavxanmcwfelwqwsesr.supabase.co';
const DEMO_API_URL = `${SUPABASE_URL}/functions/v1/demo-session`;

console.log('[Demo Client] SUPABASE_URL:', SUPABASE_URL);
console.log('[Demo Client] DEMO_API_URL:', DEMO_API_URL);

export interface DemoSessionData {
    sessionToken: string;
    expiresAt: string;
}

export interface UploadResult {
    uploadId: string;
    gcsPath: string;
    signedUrl: string;
}

export interface GenerateResult {
    renderId: string;
    variants: Array<{
        id: string;
        url: string;
        label: string;
        gcsPath: string;
    }>;
    originalUrl: string;
}

export interface ClaimResult {
    success: boolean;
    userId: string;
    renders: any[];
    creditsRemaining: number;
}

export const demoSessionClient = {
    /**
     * Create a new demo session
     */
    async create(): Promise<DemoSessionData> {
        const res = await fetch(`${DEMO_API_URL}/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${publicAnonKey}`,
                'apikey': publicAnonKey,
            },
        });

        if (!res.ok) {
            const error = await res.json();
            throw new Error(error.error || 'Failed to create session');
        }

        return await res.json();
    },

    /**
     * Upload an image to demo session
     */
    async upload(sessionToken: string, file: File): Promise<UploadResult> {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('sessionToken', sessionToken);

        const res = await fetch(`${DEMO_API_URL}/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${publicAnonKey}`,
                'apikey': publicAnonKey,
            },
            body: formData,
        });

        if (!res.ok) {
            const error = await res.json();
            throw new Error(error.error || 'Upload failed');
        }

        return await res.json();
    },

    /**
     * Generate render for demo session
     */
    async generate(
        sessionToken: string,
        gcsPath: string,
        mode: string,
        settings?: any,
        roomName?: string
    ): Promise<GenerateResult> {
        const res = await fetch(`${DEMO_API_URL}/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Demo-Session-Token': sessionToken,
                'Authorization': `Bearer ${publicAnonKey}`,
                'apikey': publicAnonKey,
            },
            body: JSON.stringify({
                sessionToken,
                gcsPath,
                mode,
                settings,
                roomName,
            }),
        });

        if (!res.ok) {
            const error = await res.json();
            throw new Error(error.error || 'Generation failed');
        }

        return await res.json();
    },

    /**
     * Claim demo session and transfer to user account
     * Requires authenticated user
     */
    async claim(sessionToken: string, accessToken: string): Promise<ClaimResult> {
        // We pass accessToken in the body to avoid Supabase Gateway 401 errors
        // caused by Gateway trying to verify the user JWT before reaching our code.
        const res = await fetch(`${DEMO_API_URL}/claim`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${publicAnonKey}`, // Identify as anon to Gateway
                'apikey': publicAnonKey,
            },
            body: JSON.stringify({
                sessionToken,
                accessToken // Pass user token solely for internal verification
            }),
        });

        if (!res.ok) {
            const error = await res.json();

            // Handle insufficient credits specially
            if (res.status === 402) {
                throw new Error('insufficient_credits');
            }

            throw new Error(error.error || 'Claim failed');
        }

        return await res.json();
    },
};
